import { useMutation, useQuery } from "@apollo/client";
import { Table, Pagination } from "@jasper/shared";
import { ActionIcon, Group, Menu, Switch } from "@mantine/core";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { IconDots } from "@tabler/icons-react";
import {
  GET_DESIGN_REVIEW_NOTIFICATIONS,
  MARK_DESIGN_REVIEW_NOTIFICATION_AS_READ,
  MARK_DESIGN_REVIEW_NOTIFICATION_AS_UNREAD,
} from "/@/gql/";
import { DesignReviewNotification } from "../../generated/graphql";

const __TAKE__ = 10;

const DesignReviewNotificationsPage = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [pageNumber, setPageNumber] = useState<number>(
    parseInt(searchParams.get("page") ?? "0", 10)
  );
  const [hideRead, setHideRead] = useState<boolean>(true);
  const skip = pageNumber * __TAKE__;

  const { data } = useQuery(GET_DESIGN_REVIEW_NOTIFICATIONS, {
    variables: { take: __TAKE__, skip, showRead: !hideRead },
    fetchPolicy: "network-only",
  });

  const goToPage = (nextPage: number) => {
    navigate(`/notifications/design-to-review?page=${nextPage}`);
    setPageNumber(nextPage);
  };

  const [markAsRead] = useMutation(MARK_DESIGN_REVIEW_NOTIFICATION_AS_READ);
  const [markAsUnread] = useMutation(MARK_DESIGN_REVIEW_NOTIFICATION_AS_UNREAD);

  const notifications: DesignReviewNotification[] =
    data?.getDesignReviewNotifications?.notifications ?? [];
  const totalCount: number =
    data?.getDesignReviewNotifications?.totalCount ?? 0;

  const setReadState = (
    notification: DesignReviewNotification,
    isRead: boolean
  ) =>
    (isRead ? markAsRead : markAsUnread)({
      variables: { id: notification.id },
      refetchQueries: ["GetNotificationCount", "GetDesignReviewNotifications"],
    });

  return (
    <>
      <Group justify="flex-end" mb="sm">
        <Switch
          label="Hide read"
          checked={hideRead}
          onChange={event => {
            setHideRead(event.currentTarget.checked);
            goToPage(0);
          }}
        />
      </Group>
      <Table
        columns={[
          "Order reference",
          "Status",
          "Doctor",
          "Provider",
          "Date",
          "Actions",
        ]}
        rowStyles={notifications.map(notification =>
          notification.isRead ? undefined : { fontWeight: 700 }
        )}
        rows={notifications.map(notification => [
          notification.orderReference ?? "-",
          notification.status ?? "-",
          notification.doctorName ?? "-",
          notification.providerName ?? "-",
          new Date(notification.createdAt).toLocaleDateString(),
          <Menu key={notification.id}>
            <Menu.Target>
              <ActionIcon variant="subtle" c="gray">
                <IconDots />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                disabled={!notification.orderId}
                onClick={() => navigate(`/orders/${notification.orderId}`)}
              >
                View order
              </Menu.Item>
              <Menu.Item
                disabled={!notification.orderId}
                onClick={() =>
                  navigate(
                    `/orders/${notification.orderId}/designs?activeDesignId=${notification.orderDesignId}`
                  )
                }
              >
                View design
              </Menu.Item>
              {notification.isRead ? (
                <Menu.Item onClick={() => setReadState(notification, false)}>
                  Mark as unread
                </Menu.Item>
              ) : (
                <Menu.Item onClick={() => setReadState(notification, true)}>
                  Mark as read
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>,
        ])}
        eventClick={[]}
      />
      <Pagination
        pageNumber={pageNumber}
        pageSize={__TAKE__}
        totalCount={totalCount}
        onPageChange={goToPage}
      />
    </>
  );
};

export default DesignReviewNotificationsPage;
